import React, { useEffect, useMemo, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { rupiah, dateTimeFmt } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Tags, Loader2, Save, History } from "lucide-react";
import { Paginator, usePaged } from "@/components/Paginator";

const PER_PAGE = 20;

export default function RegionPrices() {
  const { user } = useAuth();
  const isOwner = user.role === "super_admin";

  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [regionName, setRegionName] = useState("");
  const [items, setItems] = useState([]);
  const [edited, setEdited] = useState({}); // { pid: { unit: "value" } }
  const [initial, setInitial] = useState({}); // snapshot for diffing
  const [cats, setCats] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  const openHistory = () => {
    setHistOpen(true);
    setHistLoading(true);
    client.get("/region-prices/history", { params: regionId ? { region_id: regionId } : {} })
      .then((r) => setHistory(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setHistLoading(false));
  };

  useEffect(() => {
    client.get("/categories").then((r) => setCats(r.data));
    client.get("/price-regions").then((r) => {
      setRegions(r.data);
      if (r.data.length) setRegionId(r.data[0].region_id);
    });
  }, []);

  const load = (rid) => {
    setLoading(true);
    client.get("/region-prices", { params: rid ? { region_id: rid } : {} })
      .then((r) => {
        setItems(r.data.items);
        setRegionName(r.data.region_name);
        const init = {};
        r.data.items.forEach((p) => {
          init[p.product_id] = {};
          p.units.forEach((u) => { init[p.product_id][u.name] = u.region_price != null ? String(u.region_price) : ""; });
        });
        setEdited(init);
        setInitial(JSON.parse(JSON.stringify(init)));
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (regionId || !isOwner) load(regionId); /* eslint-disable-next-line */ }, [regionId]);

  const setPrice = (pid, unit, val) =>
    setEdited((s) => ({ ...s, [pid]: { ...(s[pid] || {}), [unit]: val } }));

  const filtered = useMemo(() => items.filter((p) =>
    p.name.toLowerCase().includes(q.toLowerCase()) &&
    (catFilter === "all" || p.category_id === catFilter)
  ), [items, q, catFilter]);

  const { page, setPage, pages, total, pageSize, slice: paged } = usePaged(filtered, PER_PAGE);
  useEffect(() => { setPage(1); }, [q, catFilter, regionId]);

  const unpricedCount = items.filter((p) =>
    p.units.every((u) => !(edited[p.product_id] && edited[p.product_id][u.name]))
  ).length;

  const save = async () => {
    setSaving(true);
    try {
      const payload = items
        .filter((p) => p.units.some((u) => {
          const cur = (edited[p.product_id] && edited[p.product_id][u.name]) ?? "";
          const orig = (initial[p.product_id] && initial[p.product_id][u.name]) ?? "";
          return String(cur) !== String(orig);
        }))
        .map((p) => {
          const prices = {};
          p.units.forEach((u) => {
            const v = edited[p.product_id] && edited[p.product_id][u.name];
            if (v !== "" && v != null) prices[u.name] = Number(v);
          });
          return { product_id: p.product_id, prices };
        });
      if (!payload.length) { toast.info("Tidak ada perubahan harga"); setSaving(false); return; }
      await client.put("/region-prices", { region_id: regionId || (regions[0] && regions[0].region_id) || "", items: payload });
      toast.success(`Harga jual region tersimpan (${payload.length} produk)`);
      load(regionId);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 space-y-5 animate-in-up" data-testid="region-prices-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Harga Jual Region</h1>
          <p className="text-sm text-muted-foreground">
            Atur harga jual per region. Produk tanpa harga tidak dapat dijual di kasir region tersebut.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openHistory} data-testid="open-region-price-history">
            <History className="mr-2 h-4 w-4" /> Riwayat
          </Button>
          <Button onClick={save} disabled={saving || loading} data-testid="save-region-prices">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Simpan Harga
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {isOwner && (
          <Select value={regionId} onValueChange={setRegionId}>
            <SelectTrigger className="w-56" data-testid="region-select"><SelectValue placeholder="Pilih Region" /></SelectTrigger>
            <SelectContent>
              {regions.map((r) => <SelectItem key={r.region_id} value={r.region_id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {!isOwner && (
          <Badge variant="secondary" className="gap-1" data-testid="region-label"><Tags className="h-3.5 w-3.5" /> {regionName}</Badge>
        )}
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk…" className="pl-9" data-testid="region-price-search" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-48" data-testid="region-price-cat-filter"><SelectValue placeholder="Semua Kategori" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Badge variant="outline" data-testid="unpriced-count">{unpricedCount} produk belum berharga</Badge>
      </div>

      <div className="rounded-lg border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produk</TableHead>
              <TableHead>Satuan</TableHead>
              <TableHead className="text-right">Harga Default</TableHead>
              <TableHead className="w-48 text-right">Harga Jual Region</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((p) => (
              p.units.map((u, ui) => (
                <TableRow key={`${p.product_id}-${u.name}`} className="hover:bg-slate-50" data-testid={`region-price-row-${p.product_id}-${u.name}`}>
                  {ui === 0 && <TableCell rowSpan={p.units.length} className="font-medium align-top">{p.name}</TableCell>}
                  <TableCell>{u.name}</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">{rupiah(u.default_price)}</TableCell>
                  <TableCell className="text-right">
                    <Input type="number" value={(edited[p.product_id] && edited[p.product_id][u.name]) ?? ""}
                      onChange={(e) => setPrice(p.product_id, u.name, e.target.value)}
                      className="h-9 w-36 text-right font-mono" placeholder="belum diatur"
                      data-testid={`region-price-input-${p.product_id}-${u.name}`} />
                  </TableCell>
                </TableRow>
              ))
            ))}
            {!paged.length && <TableRow><TableCell colSpan={4} className="py-10 text-center text-muted-foreground">Tidak ada produk</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Paginator page={page} setPage={setPage} pages={pages} total={total} pageSize={pageSize} />
      <p className="text-xs text-muted-foreground">
        Kolom "Harga Jual Region" kosong = produk belum berharga di region ini dan tidak dapat dijual di kasir. Harga default hanya rujukan, bukan harga yang dipakai.
      </p>

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-2xl" data-testid="region-price-history-dialog">
          <DialogHeader>
            <DialogTitle>Riwayat Harga Region</DialogTitle>
            <DialogDescription>Perubahan harga jual{isOwner ? ` region ${regionName}` : ""} — siapa & kapan.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Waktu</TableHead>
                  <TableHead>Produk</TableHead>
                  <TableHead>Satuan</TableHead>
                  <TableHead className="text-right">Perubahan</TableHead>
                  <TableHead>Oleh</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {histLoading && <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></TableCell></TableRow>}
                {!histLoading && history.map((h) => (
                  <TableRow key={h.id} data-testid={`history-row-${h.id}`}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{dateTimeFmt(h.created_at)}</TableCell>
                    <TableCell className="font-medium">{h.product_name}</TableCell>
                    <TableCell>{h.unit}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      <span className="text-muted-foreground line-through">{h.old_price != null ? rupiah(h.old_price) : "—"}</span>
                      {" → "}
                      <span className={h.new_price != null ? "font-semibold text-primary" : "font-semibold text-rose-600"}>{h.new_price != null ? rupiah(h.new_price) : "dihapus"}</span>
                    </TableCell>
                    <TableCell className="text-xs">{h.user_name}</TableCell>
                  </TableRow>
                ))}
                {!histLoading && !history.length && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">Belum ada riwayat perubahan</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
